/**
 * A small symbol in a symbol frame (TemplateZone.symbolOnly, 2026-10-31): a heart, a star or a
 * half moon, in one colour - the square letter's seal on the back and the star in the front's
 * point, where the owner asked for "an area where the user can add a shape".
 *
 * Drawn as a path, never as a character, so it looks the same in every browser, on the print and
 * in the PDF. Each shape fills a circle of radius 1 pointing up (-y); the frame's `symbolAt` says
 * where that circle is on the printed page and how big, and its `up` which way the symbol stands.
 */
import type { ProjectDocument, TemplateZone, ZoneSymbol } from '@foldmycard/scene';

export const SYMBOL_SHAPES: ZoneSymbol['shape'][] = ['heart', 'star', 'moon'];
/** Bright enough to read as a seal on white paper; the last one is ink. */
export const SYMBOL_COLOURS = ['#d6334a', '#e86a92', '#e0a526', '#3b8fd9', '#2e9e5b', '#1b2430'];

const PATHS: Record<ZoneSymbol['shape'], string> = {
  heart: 'M0 0.85 C-0.25 0.6 -0.95 0.2 -0.95 -0.3 C-0.95 -0.7 -0.6 -0.95 -0.3 -0.95 C-0.1 -0.95 0 -0.8 0 -0.65 '
    + 'C0 -0.8 0.1 -0.95 0.3 -0.95 C0.6 -0.95 0.95 -0.7 0.95 -0.3 C0.95 0.2 0.25 0.6 0 0.85 Z',
  star: 'M0 -1 L0.235 -0.324 L0.951 -0.309 L0.38 0.124 L0.588 0.809 L0 0.4 L-0.588 0.809 L-0.38 0.124 L-0.951 -0.309 L-0.235 -0.324 Z',
  // a fat crescent, the two tips to the right: the outer edge of a circle and the inner edge of another
  moon: 'M0.609 -0.742 A0.9 0.9 0 1 0 0.609 0.742 A0.75 0.75 0 1 1 0.609 -0.742 Z',
};

/** The symbol, drawn on the printed page of its frame (the caller clips it to the frame). */
export function SymbolMark({ zone, symbol }: { zone: TemplateZone; symbol: ZoneSymbol }) {
  if (!zone.symbolAt) return null;
  const { at, radiusMm } = zone.symbolAt;
  // the path points up the page (bearing -90 degrees): turn it to the frame's own up
  return (
    <path d={PATHS[symbol.shape]} fill={symbol.color}
      transform={`translate(${at[0]} ${at[1]}) rotate(${zone.upBearingDeg + 90}) scale(${radiusMm})`} />
  );
}

/** The same shape as an icon for a button, in a 24 x 24 box. */
export function SymbolIcon({ shape, color = 'currentColor' }: { shape: ZoneSymbol['shape']; color?: string }) {
  return (
    <svg viewBox="-1.1 -1.1 2.2 2.2" width={20} height={20} aria-hidden="true">
      <path d={PATHS[shape]} fill={color} />
    </svg>
  );
}

/** The card's symbols by frame number: the frames that hold one, and are not left plain. */
export function symbolsOf(doc: ProjectDocument | null | undefined): Map<number, ZoneSymbol> {
  const m = new Map<number, ZoneSymbol>();
  for (const [k, slot] of Object.entries(doc?.zones || {})) if (slot.symbol && !slot.plain) m.set(Number(k), slot.symbol);
  return m;
}
